import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import logo from '../Assets/Logo.png';
import RegisterModal from './Register2';
import Loader from './Loader';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import useLogin from '../Hooks/LoginHooks/useLogin2';
import usePasswordToggle from '../Hooks/LoginHooks/usePasswordToggle';

const GlobalStyle = createGlobalStyle`
  @keyframes shine {
    0% { transform: translateX(-100%) rotate(25deg); }
    100% { transform: translateX(100%) rotate(25deg); }
  }
`;

const LoginModal = ({ isOpen, toggleModal }) => {
  const {
    code,
    setCode,
    password,
    setPassword,
    error,
    setError,
    loading,
    handleLogin,
  } = useLogin();

  const { showPassword, togglePasswordVisibility } = usePasswordToggle();
  const [isLoading, setIsLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleLoginClick = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await handleLogin(e);
    } catch (err) {
      setError("Invalid credentials. Please try again.");
      toast.error("Invalid credentials. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRegister = () => setShowRegister(true);
  const handleCloseRegister = () => setShowRegister(false);

  return (
    <>
      <GlobalStyle />
      <ToastContainer />
      {showRegister ? (
        <RegisterModal isOpen={true} closeRegister={handleCloseRegister} />
      ) : (
        <Modal>
          <ModalContent>
            <RightContainer>
              <CurvedTop />
              <LogoWrapper>
                <LogoFlip>
                  <LogoFace src={logo} alt="Logo Front" />
                  <LogoBack src={logo} alt="Logo Back" />
                </LogoFlip>
              </LogoWrapper>
              <LoginTitle>LOGIN</LoginTitle>
              <CloseButton onClick={toggleModal}>&times;</CloseButton>

              <SignInForm onSubmit={handleLoginClick}>
                <FormField>
                  <InputLabel>User Code</InputLabel>
                  <Input
                    type="number"
                    placeholder="Enter your user code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </FormField>

                <FormField>
                  <InputLabel>Password</InputLabel>
                  <PasswordInputWrapper>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <PasswordToggleIcon onClick={togglePasswordVisibility}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </PasswordToggleIcon>
                  </PasswordInputWrapper>
                </FormField>

                <ForgotPasswordLink href="#">Forgot Password?</ForgotPasswordLink>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Logging in...' : 'Log In'}
                </SubmitButton>

                {isLoading && (
                  <LoaderWrapper>
                    <Loader />
                  </LoaderWrapper>
                )}

              </SignInForm>
            </RightContainer>
          </ModalContent>
          {isLoading && <Overlay />}
        </Modal>
      )}
    </>
  );
};

export default LoginModal;



// ========== Styled Components ==========

const Modal = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  display: flex; justify-content: center; align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 5;
`;

const ModalContent = styled.div`
  width: 50%; max-width: 500px; height: 80%;
  display: flex; flex-direction: column;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.95);
  overflow: hidden; position: relative;
`;

const RightContainer = styled.div`
  display: flex; flex-direction: column; align-items: center;
  width: 100%; padding: 30px 20px 20px;
  position: relative;
`;

const CurvedTop = styled.div`
  position: absolute; top: 0;
  width: 100%; height: 150px;
  background: linear-gradient(45deg, #18591d, #00ff7f);
  border-bottom-left-radius: 50% 75%;
  border-bottom-right-radius: 50% 75%;
  overflow: hidden;
  z-index: 0;

  &::after {
    content: ''; position: absolute;
    top: -50%; left: -75%;
    width: 200%; height: 200%;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    transform: rotate(25deg);
    animation: shine 3s linear infinite;
  }
`;

const LogoWrapper = styled.div`
  perspective: 1000px;
  width: 80px; height: 80px;
  margin-top: -9px;
  z-index: 2;
`;

const LogoFlip = styled.div`
  width: 100%; height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: flipLogo 6s linear infinite;

  @keyframes flipLogo {
    0% { transform: rotateY(0deg); }
    100% { transform: rotateY(360deg); }
  }
`;

const LogoFace = styled.img`
  position: absolute;
  width: 100%; height: 100%;
  backface-visibility: hidden;
  border-radius: 50%;
`;

const LogoBack = styled(LogoFace)`
  transform: rotateY(180deg);
`;

const LoginTitle = styled.h2`
  position: absolute;
  top: 110px; left: 50%;
  transform: translateX(-50%);
  font-size: 1.5rem; font-weight: 700;
  color: white; z-index: 2;
`;

const CloseButton = styled.span`
  position: absolute; top: 20px; right: 20px;
  font-size: 2.5rem;
  color: green; cursor: pointer;
`;

const SignInForm = styled.form`
  display: flex; flex-direction: column; align-items: center;
  width: 80%; margin-top: 90px;
`;

const FormField = styled.div`
  margin: 15px 0 10px;
  width: 100%;
`;

const InputLabel = styled.label`
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
  color: #000;
`;

const Input = styled.input`
  font-family: 'Poppins';
  width: 100%; padding: 10px;
  border-radius: 8px; font-size: 1.1rem;
  border: 1px solid #c4c4c4;

  &:focus {
    border-color: #185faf;
    box-shadow: 0 0 10px rgba(24, 89, 29, 0.5);
    outline: none;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggleIcon = styled.span`
  position: absolute;
  right: 10px; top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 20px;
`;

const ForgotPasswordLink = styled.a`
  align-self: flex-end;
  font-size: 0.8rem;
  font-weight: bold;
  color: #0c0a0a;
  margin-bottom: 15px;
`;

const SubmitButton = styled.button`
  width: 100%; padding: 13px;
  background: linear-gradient(45deg, #18591d, #00ff7f);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 1.2rem;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  font-family: 'Poppins';

  &::after {
    content: ''; position: absolute;
    top: -50%; left: -75%;
    width: 200%; height: 200%;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    transform: rotate(25deg);
    animation: shine 3s linear infinite;
    z-index: 2;
  }

  &:hover {
    transform: scale(1.05);
    transition: all 0.3s ease;
  }
`;

const LoaderWrapper = styled.div`
  position: absolute; top: 0; left: 0;
  right: 0; bottom: 0;
  display: flex; justify-content: center; align-items: center;
  z-index: 1100;
`;

const Overlay = styled.div`
  position: absolute; top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  
`;
